import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Vehicle } from '@typeRoots/common/vehicles';

// Define the VehiclesState type
interface VehiclesState {
  vehicles: Partial<Vehicle>[]; // List of vehicles
  isDataSyncedToBackend: boolean; // Tracks unsaved changes
  currentVehicle: Partial<Vehicle> | null; // Tracks the vehicle being added or edited
  showVehicleToast: boolean;
  lastRadiusTravelled: string; // Remembers last chosen radius to prefill the next new vehicle
}

// Initial state
const initialState: VehiclesState = {
  vehicles: [], // Start with an empty array
  isDataSyncedToBackend: true, // No unsaved changes
  currentVehicle: null, // No current vehicle initially
  showVehicleToast: false,
  lastRadiusTravelled: '', // No remembered radius initially
};

// Create the slice
export const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    // Set vehicles array from backend data
    setAllVehicles(state, action: PayloadAction<Partial<Vehicle>[]>) {
      state.vehicles = action.payload;
      state.isDataSyncedToBackend = true; // No unsaved changes
    },

    // Add a new vehicle to the vehicles array
    setVehicle(state, action: PayloadAction<Partial<Vehicle>>) {
      if (state.vehicles === null || state.vehicles === undefined) {
        state.vehicles = [];
      }
      // Add the new vehicle to the vehicles array
      state.vehicles.push(action.payload);
      state.isDataSyncedToBackend = true; // Mark as dirty due to the new addition
      state.currentVehicle = null; // Clear currentVehicle after adding
    },

    // Set the currentVehicle when adding or editing a vehicle
    setCurrentVehicle(state, action: PayloadAction<Partial<Vehicle>>) {
      state.currentVehicle = action.payload; // Track current vehicle for add/edit
    },

    // Update a specific vehicle by merging its new properties
    updateVehicle(state, action: PayloadAction<Partial<Vehicle> & { id: number }>) {
      const { id, ...updates } = action.payload;
      if (state.vehicles) {
        const index = state.vehicles.findIndex((vehicle) => vehicle.id === id);
        if (index !== -1) {
          state.vehicles[index] = { ...state.vehicles[index], ...updates };
          state.isDataSyncedToBackend = true; // Mark as dirty due to updates
        }
      }
      state.currentVehicle = null; // Clear currentVehicle after updating
    },

    // Mark vehicles as saved
    updateIsSynced(state, action: PayloadAction<boolean>) {
      state.isDataSyncedToBackend = action.payload; // Data is now saved
    },

    // Clear a specific vehicle by its ID
    deleteVehicle(state, action: PayloadAction<number>) {
      if (state.vehicles) {
        state.vehicles = state.vehicles.filter((vehicle) => vehicle.id !== action.payload);
        state.isDataSyncedToBackend = true; // Mark as dirty after deletion
      }
      state.currentVehicle = null; // Clear currentVehicle after deleting
    },

    // Optionally, clear all vehicles (if needed)
    clearAllVehicles(state) {
      state.vehicles = [];
      state.isDataSyncedToBackend = false;
      state.currentVehicle = null;
    },
    toggleVehicleToast(state, action: PayloadAction<boolean>) {
      state.showVehicleToast = action.payload;
    },

    // Remember the last radius chosen so the next new vehicle can prefill it
    setLastRadiusTravelled(state, action: PayloadAction<string>) {
      state.lastRadiusTravelled = action.payload;
    },
  },
});

// Export actions
export const {
  setAllVehicles,
  updateVehicle,
  updateIsSynced,
  deleteVehicle,
  setVehicle,
  setCurrentVehicle,
  clearAllVehicles,
  toggleVehicleToast,
  setLastRadiusTravelled,
} = vehiclesSlice.actions;

// Export reducer
export default vehiclesSlice.reducer;
